<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <b-card-body class="p-50">
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-50 font-weight-bold text-nowrap">
            Closing Balance:
          </label>
          <span class="balance-display font-weight-bold">
            {{ todayBalance.amount !== null ? formatAmount(todayBalance.amount) : '-' }}
          </span>

          <!-- Add/Edit Button -->
          <b-dropdown
            ref="formDropdown"
            variant="link"
            no-caret
            right
            toggle-class="p-0 text-decoration-none ml-50"
            menu-class="dropdown-menu-form"
            :no-close-on-select="true"
            @show="onFormDropdownShow"
            @hide="onFormDropdownHide"
          >
            <template #button-content>
              <feather-icon
                :icon="todayBalance.id ? 'EditIcon' : 'PlusIcon'"
                size="20"
                class="cursor-pointer text-primary"
              />
            </template>

            <div
              class="dropdown-content-wrapper"
              @click.stop
              @mousedown.stop
            >
              <!-- Form Section -->
              <div class="form-section p-50 border-form">
                <h6 class="mb-50 font-weight-bold">
                  {{ todayBalance.id ? 'Edit' : 'Add' }} Closing Balance
                </h6>

                <b-form @submit.prevent="saveTodayBalance">
                  <!-- Amount Field -->
                  <b-form-group
                    label="Total Amount"
                    label-class="font-weight-bold"
                    class="mb-50"
                  >
                    <b-form-input
                      v-model.number="formData.amount"
                      type="number"
                      size="sm"
                      placeholder="Enter Amount"
                      :disabled="isProcessing"
                      readonly
                      class="bg-light"
                    />
                  </b-form-group>

                  <!-- Denominations Table -->
                  <div class="denominations-section mb-50">
                    <label class="font-weight-bold d-block mb-25">Denominations</label>
                    <table class="table table-sm table-bordered mb-0 denominations-table">
                      <thead>
                        <tr class="bg-light">
                          <th
                            v-for="denom in denominations"
                            :key="denom.key"
                            class="text-center"
                          >
                            ₹{{ denom.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            v-for="denom in denominations"
                            :key="denom.key"
                            class="p-25"
                          >
                            <b-form-input
                              v-model.number="formData[denom.key]"
                              type="number"
                              size="sm"
                              min="0"
                              :disabled="isProcessing"
                              class="compact-number-input"
                              @input="calculateTotal"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Action Buttons -->
                  <div class="d-flex justify-content-end">
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      class="mr-50"
                      :disabled="isProcessing"
                      @click="closeFormDropdown"
                    >
                      Cancel
                    </b-button>
                    <b-button
                      variant="primary"
                      size="sm"
                      type="submit"
                      :disabled="isProcessing || !isValidForm"
                    >
                      <b-spinner
                        v-if="isProcessing"
                        small
                        class="mr-50"
                      />
                      {{ todayBalance.id ? 'Update' : 'Save' }}
                    </b-button>
                  </div>
                </b-form>
              </div>

              <!-- Date Filter Section -->
              <div class="filter-section p-50 w-100 border-table">
                <flat-picker
                  v-model="dateRange"
                  placeholder="Select Date Range"
                  :config="flatPickerConfig"
                  class="w-100"
                />
              </div>

              <!-- History Table Section -->
              <div
                v-if="dateRange"
                class="table-section border-table"
              >
                <table class="table table-sm table-bordered mb-0">
                  <thead>
                    <tr class="bg-light">
                      <th style="width: 8%;">
                        #
                      </th>
                      <th style="width: 20%;">
                        Date
                      </th>
                      <th
                        style="width: 22%;"
                        class="text-right"
                      >
                        Amount (₹)
                      </th>
                      <th style="width: 25%;">
                        Created By
                      </th>
                      <th
                        style="width: 25%;"
                        class="text-center"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in flattenedHistoryRows"
                      :key="row.id"
                      :class="{ 'bg-light': row.type === 'detail' }"
                    >
                      <template v-if="row.type === 'main'">
                        <td>{{ row.index + 1 }}</td>
                        <td>{{ formatedDate(row.record.date, 'DD-MM-YYYY') }}</td>
                        <td class="text-right">
                          {{ formatAmount(row.record.amount) }}
                        </td>
                        <td>{{ row.record.created_by_name || '-' }}</td>
                        <td class="text-center">
                          <feather-icon
                            :icon="expandedRecords.includes(row.record.id) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            size="16"
                            class="cursor-pointer text-primary"
                            @click="toggleRecordExpansion(row.record.id)"
                          />
                        </td>
                      </template>
                      <template v-else>
                        <td colspan="5">
                          <div class="denominations-detail p-50">
                            <div class="d-flex flex-wrap">
                              <div
                                v-for="denom in denominations"
                                :key="denom.key"
                                class="denom-detail-item"
                              >
                                <span class="text-muted">₹{{ denom.label }}:</span>
                                <span class="font-weight-bold ml-25">{{ row.record[denom.key] || 0 }}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </template>
                    </tr>

                    <tr v-if="historyRecords.length === 0 && !loadingHistory">
                      <td
                        colspan="5"
                        class="text-center text-muted"
                      >
                        No records found
                      </td>
                    </tr>

                    <tr v-if="loadingHistory">
                      <td
                        colspan="5"
                        class="text-center"
                      >
                        <b-spinner small />
                        Loading...
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-dropdown>

          <!-- History Only Dropdown -->
          <b-dropdown
            ref="historyDropdown"
            variant="link"
            no-caret
            right
            toggle-class="p-0 text-decoration-none ml-50"
            menu-class="dropdown-menu-history"
            :no-close-on-select="true"
            @show="onHistoryDropdownShow"
            @hide="onHistoryDropdownHide"
          >
            <template #button-content>
              <feather-icon
                :icon="historyDropdownOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="20"
                class="text-primary"
              />
            </template>

            <div
              class="dropdown-content-wrapper"
              @click.stop
              @mousedown.stop
            >
              <div class="filter-section p-75 w-100">
                <flat-picker
                  v-model="dateRange"
                  placeholder="Select Date Range"
                  :config="flatPickerConfig"
                  class="w-100 d-flex justify-content-center"
                />
              </div>

              <div
                v-if="dateRange"
                class="table-section"
              >
                <table class="table table-sm table-bordered mb-0">
                  <thead>
                    <tr class="bg-light">
                      <th style="width: 8%;">
                        #
                      </th>
                      <th style="width: 20%;">
                        Date
                      </th>
                      <th
                        style="width: 22%;"
                        class="text-right"
                      >
                        Amount (₹)
                      </th>
                      <th style="width: 25%;">
                        Created By
                      </th>
                      <th
                        style="width: 25%;"
                        class="text-center"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in flattenedHistoryRows"
                      :key="row.id"
                      :class="{ 'bg-light': row.type === 'detail' }"
                    >
                      <template v-if="row.type === 'main'">
                        <td>{{ row.index + 1 }}</td>
                        <td>{{ formatedDate(row.record.date, 'DD-MM-YYYY') }}</td>
                        <td class="text-right">
                          {{ formatAmount(row.record.amount) }}
                        </td>
                        <td>{{ row.record.created_by_name || '-' }}</td>
                        <td class="text-center">
                          <feather-icon
                            :icon="expandedRecords.includes(row.record.id) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            size="16"
                            class="cursor-pointer text-primary"
                            @click="toggleRecordExpansion(row.record.id)"
                          />
                        </td>
                      </template>
                      <template v-else>
                        <td colspan="5">
                          <div class="denominations-detail p-50">
                            <div class="d-flex flex-wrap">
                              <div
                                v-for="denom in denominations"
                                :key="denom.key"
                                class="denom-detail-item"
                              >
                                <span class="text-muted">₹{{ denom.label }}:</span>
                                <span class="font-weight-bold ml-25">{{ row.record[denom.key] || 0 }}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </template>
                    </tr>

                    <tr v-if="historyRecords.length === 0 && !loadingHistory">
                      <td
                        colspan="5"
                        class="text-center text-muted"
                      >
                        No records found
                      </td>
                    </tr>

                    <tr v-if="loadingHistory">
                      <td
                        colspan="5"
                        class="text-center"
                      >
                        <b-spinner small />
                        Loading...
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-dropdown>
        </div>

        <b-alert
          v-if="errorMessage"
          variant="danger"
          show
          dismissible
          class="mt-50 py-50"
          @dismissed="errorMessage = null"
        >
          {{ errorMessage }}
        </b-alert>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BFormInput,
  BFormGroup,
  BButton,
  BForm,
  BSpinner,
  BAlert,
  BDropdown,
} from 'bootstrap-vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'

export default {
  components: {
    BCard,
    BCardBody,
    BFormInput,
    BFormGroup,
    BButton,
    BForm,
    BSpinner,
    BAlert,
    BDropdown,
    FlatPicker,
  },
  mixins: [dateMixin, formatMixin],
  data() {
    return {
      todayBalance: {
        id: null,
        amount: null,
        date: this.formatedDate(new Date(), 'YYYY-MM-DD'),
      },
      formData: {
        amount: 0,
        c_500: 0,
        c_200: 0,
        c_100: 0,
        c_50: 0,
        c_20: 0,
        c_10: 0,
        c_5: 0,
        c_2: 0,
        c_1: 0,
      },
      denominations: [
        { key: 'c_500', label: '500', value: 500 },
        { key: 'c_200', label: '200', value: 200 },
        { key: 'c_100', label: '100', value: 100 },
        { key: 'c_50', label: '50', value: 50 },
        { key: 'c_20', label: '20', value: 20 },
        { key: 'c_10', label: '10', value: 10 },
        { key: 'c_5', label: '5', value: 5 },
        { key: 'c_2', label: '2', value: 2 },
        { key: 'c_1', label: '1', value: 1 },
      ],
      dateRange: null,
      historyRecords: [],
      expandedRecords: [],
      loadingHistory: false,
      isProcessing: false,
      errorMessage: null,
      formDropdownOpen: false,
      historyDropdownOpen: false,
      flatPickerConfig: {
        mode: 'range',
        static: true,
      },
    }
  },
  computed: {
    isValidForm() {
      return this.formData.amount >= 0
    },
    flattenedHistoryRows() {
      const rows = []
      this.historyRecords.forEach((record, index) => {
        // Add main row
        rows.push({
          type: 'main',
          record,
          index,
          id: `main-${record.id}`,
        })
        // Add detail row if expanded
        if (this.expandedRecords.includes(record.id)) {
          rows.push({
            type: 'detail',
            record,
            id: `detail-${record.id}`,
          })
        }
      })
      return rows
    },
  },
  watch: {
    dateRange(newVal) {
      if (newVal) {
        this.fetchHistory()
      }
    },
  },
  mounted() {
    this.fetchTodayBalance()
    this.setDefaultDateRange()
  },
  methods: {
    calculateTotal() {
      let total = 0
      this.denominations.forEach(denom => {
        const count = this.formData[denom.key] || 0
        total += count * denom.value
      })
      this.formData.amount = total
    },
    toggleRecordExpansion(recordId) {
      const index = this.expandedRecords.indexOf(recordId)
      if (index > -1) {
        this.expandedRecords.splice(index, 1)
      } else {
        this.expandedRecords.push(recordId)
      }
    },
    onFormDropdownShow() {
      this.formDropdownOpen = true
      // Load existing data if editing
      if (this.todayBalance.id) {
        this.loadFormData()
      } else {
        this.resetFormData()
      }
    },
    onFormDropdownHide() {
      this.formDropdownOpen = false
    },
    onHistoryDropdownShow() {
      this.historyDropdownOpen = true
    },
    onHistoryDropdownHide() {
      this.historyDropdownOpen = false
    },
    closeFormDropdown() {
      if (this.$refs.formDropdown) {
        this.$refs.formDropdown.hide()
      }
    },
    resetFormData() {
      this.formData = {
        amount: 0,
        c_500: 0,
        c_200: 0,
        c_100: 0,
        c_50: 0,
        c_20: 0,
        c_10: 0,
        c_5: 0,
        c_2: 0,
        c_1: 0,
      }
    },
    async loadFormData() {
      try {
        const response = await axios.get(`/operations/closing_balance/${this.todayBalance.id}/`)
        const record = response.data
        this.formData = {
          amount: record.amount || 0,
          c_500: record.c_500 || 0,
          c_200: record.c_200 || 0,
          c_100: record.c_100 || 0,
          c_50: record.c_50 || 0,
          c_20: record.c_20 || 0,
          c_10: record.c_10 || 0,
          c_5: record.c_5 || 0,
          c_2: record.c_2 || 0,
          c_1: record.c_1 || 0,
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to load record details'
      }
    },
    setDefaultDateRange() {
      const today = new Date()
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(today.getDate() - 7)

      const startDate = this.formatedDate(sevenDaysAgo, 'YYYY-MM-DD')
      const endDate = this.formatedDate(today, 'YYYY-MM-DD')

      this.dateRange = `${startDate} to ${endDate}`
      // Fetch history immediately after setting default range
      this.$nextTick(() => {
        this.fetchHistory()
      })
    },
    async fetchTodayBalance() {
      try {
        const todayStr = this.formatedDate(new Date(), 'YYYY-MM-DD')
        const response = await axios.get(`/operations/closing_balance/?${getParsedQueryString({
          start_date: todayStr,
          end_date: todayStr,
        })}`)
        if (response.data && response.data.results && response.data.results.length > 0) {
          const record = response.data.results[0]
          this.todayBalance = {
            id: record.id,
            amount: record.amount,
            date: record.date,
          }
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to load closing balance'
      }
    },
    async saveTodayBalance() {
      if (!this.isValidForm || this.isProcessing) return

      this.isProcessing = true
      this.errorMessage = null

      try {
        const payload = { ...this.formData }

        if (this.todayBalance.id) {
          await axios.patch(`/operations/closing_balance/${this.todayBalance.id}/`, payload)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Closing balance updated',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          const response = await axios.post('/operations/closing_balance/', payload)
          this.todayBalance.id = response.data.id
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Closing balance added',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }

        // Update today's balance display
        this.todayBalance.amount = this.formData.amount

        // Refresh history after save
        if (this.dateRange) {
          await this.fetchHistory()
        }

        this.closeFormDropdown()
        this.$emit('data-updated')
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to save'
      } finally {
        this.isProcessing = false
      }
    },
    async fetchHistory() {
      if (!this.dateRange) return

      this.loadingHistory = true
      this.errorMessage = null

      try {
        const dates = this.dateRange.split(' to ')
        const params = {}

        if (dates.length === 2) {
          // Date range selected
          params.start_date = dates[0].trim()
          params.end_date = dates[1].trim()
        } else if (dates.length === 1 && dates[0]) {
          // Single date selected
          params.start_date = dates[0].trim()
          params.end_date = dates[0].trim()
        }

        params.per_page = 100

        const response = await axios.get(`/operations/closing_balance/?${getParsedQueryString(params)}`)
        this.historyRecords = response.data.results || []
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to load history'
        this.historyRecords = []
      } finally {
        this.loadingHistory = false
      }
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },
}
</script>

<style scoped>
.balance-display {
  min-width: 100px;
  padding: 0.25rem 0.5rem;
  background-color: #f8f8f8;
  border-radius: 0.25rem;
  font-size: 1rem;
  color: #5e5873;
}

.p-25 {
  padding: 0.25rem !important;
}

.p-50 {
  padding: 0.4rem !important;
}

.p-75 {
  padding: 0.75rem !important;
}

.mb-25 {
  margin-bottom: 0.25rem !important;
}

.mb-50 {
  margin-bottom: 0.5rem !important;
}

.mb-75 {
  margin-bottom: 0.75rem !important;
}

.mt-50 {
  margin-top: 0.5rem !important;
}

.mr-25 {
  margin-right: 0.25rem !important;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.ml-25 {
  margin-left: 0.25rem !important;
}

.ml-50 {
  margin-left: 0.5rem !important;
}

.border-danger {
  border-color: #ea5455 !important;
}

.text-sm {
  font-size: 0.875rem;
}

/* Form Section */
.form-section {
  border-bottom: 1px solid #ebe9f1;
}

.border-form {
  border: 1px solid #7367f0 !important;
  border-radius: 0.25rem;
}

.form-section h6 {
  color: #5e5873;
  font-size: 0.9rem;
  line-height: 1.2;
}

.form-section .form-group {
  margin-bottom: 0.5rem;
}

.form-section .form-group label {
  margin-bottom: 0.25rem;
  font-size: 0.85rem;
}

/* Border for table/filter sections */
.border-table {
  border-left: 1px solid #28c76f !important;
  border-right: 1px solid #28c76f !important;
}

.filter-section.border-table {
  border-top: 1px solid #28c76f !important;
}

.table-section.border-table {
  border-bottom: 1px solid #28c76f !important;
}

/* Denominations Table - Excel-like Layout */
.denominations-section {
  background-color: #f8f8f8;
  padding: 0.35rem;
  border-radius: 0.25rem;
}

.denominations-table {
  background-color: white;
  border-collapse: collapse;
  margin-bottom: 0 !important;
}

.denominations-table th {
  padding: 0.15rem 0.1rem !important;
  font-size: 0.7rem;
  font-weight: 600;
  vertical-align: middle;
  white-space: nowrap;
  background-color: #f3f2f7;
  border: 1px solid #d8d6de;
  line-height: 1.2;
}

.denominations-table td {
  padding: 0 !important;
  margin: 0 !important;
  vertical-align: middle;
  border: 1px solid #d8d6de;
  line-height: 1;
  height: 26px;
}

/* Excel-like input style - no borders, seamless look */
.compact-number-input {
  font-size: 0.7rem !important;
  padding: 0.25rem 0.2rem !important;
  height: 26px !important;
  line-height: 1 !important;
  text-align: center;
  min-width: 35px;
  margin: 0 !important;
  border: none !important;
  background-color: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: block;
  width: 100%;
}

.compact-number-input:focus {
  border: none !important;
  background-color: #fffbea !important;
  outline: 2px solid #7367f0 !important;
  outline-offset: -2px;
  box-shadow: none !important;
}

.compact-number-input:disabled {
  background-color: #f3f2f7 !important;
}

/* Remove spinner arrows for a cleaner look */
.compact-number-input::-webkit-outer-spin-button,
.compact-number-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.compact-number-input[type=number] {
  -moz-appearance: textfield;
}

/* Denominations Detail */
.denominations-detail {
  background-color: #f8f8f8;
}

.denom-detail-item {
  width: 33.333%;
  padding: 0.25rem;
  font-size: 0.813rem;
}

/* Table Styles */
.table-sm td,
.table-sm th {
  padding: 0.3rem;
  vertical-align: middle;
  font-size: 0.875rem;
}

.dropdown-content-wrapper {
  padding: 0;
}

.filter-section {
  min-width: 200px;
}

.filter-section:not(.border-table) {
  border-bottom: 1px solid #ebe9f1;
}

.table-section {
  max-height: 350px;
  overflow-y: auto;
}

/* Dropdown Menu Styles - Force dropdown to go down */
::v-deep .dropdown-menu-form {
  padding: 0 !important;
  min-width: 520px;
  max-width: 520px;
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
}

::v-deep .dropdown-menu-history {
  padding: 0 !important;
  min-width: 500px;
  max-width: 500px;
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
}

/* Force dropdown to always appear below */
::v-deep .b-dropdown.show > .dropdown-menu {
  top: 100% !important;
  bottom: auto !important;
}

::v-deep .flatpickr-calendar {
  position: relative !important;
  top: 0 !important;
  left: 0 !important;
}

/* Flatpicker full width */
::v-deep .filter-section .flatpickr-wrapper {
  width: 80% !important;
  display: block !important;
}

::v-deep .filter-section .form-control {
  width: 100% !important;
}

::v-deep .filter-section .flatpickr-input {
  width: 80% !important;
}
</style>
